import type { OrderDetailsUI } from "@/domain/order/order-details.model";
import type { ChargeType } from "@/domain/order/charge-classification";
import { roundMoney } from "@/domain/money/round-money";
import { BILLING_MONEY_EPSILON } from "@/domain/billing/order-billing.constants";
import { resolveOrderDiscount } from "@/domain/billing/discount-resolution";
import type { BillingDisplayLine, OrderBillingViewModel } from "@/domain/billing/order-billing.types";

function bucketForChargeType(chargeType: ChargeType): "tax" | "fee" {
  if (chargeType === "TAX") return "tax";
  return "fee";
}

function toChargeLine(kind: "fee" | "tax", label: string, amount: number): BillingDisplayLine {
  return { kind, label, amount: roundMoney(amount) };
}

function sumLineAmounts(lines: ReadonlyArray<BillingDisplayLine>): number {
  return roundMoney(lines.reduce((total, line) => total + line.amount, 0));
}

/**
 * Maps normalized order details into the canonical billing view model.
 * All monetary values are rounded; discount resolution is centralized.
 */
export function mapOrderDetailsToBillingViewModel(order: OrderDetailsUI): OrderBillingViewModel {
  const { charges, summary, discounts, hasOrderBillSummary } = order;

  const itemizedTax: BillingDisplayLine[] = [];
  const itemizedFees: BillingDisplayLine[] = [];

  for (const charge of charges) {
    const bucket = bucketForChargeType(charge.chargeType);
    const line = toChargeLine(bucket, charge.name, charge.amount);
    if (bucket === "tax") {
      itemizedTax.push(line);
    } else {
      itemizedFees.push(line);
    }
  }

  const hasItemizedTax = itemizedTax.length > 0;
  const hasSummaryTax = (summary?.totalTax ?? 0) > 0;
  const taxLines: BillingDisplayLine[] = hasItemizedTax
    ? itemizedTax
    : hasSummaryTax
      ? [toChargeLine("tax", "Tax", summary.totalTax)]
      : [];

  const feeLines: BillingDisplayLine[] = [...itemizedFees];
  const feesFromItemized = sumLineAmounts(itemizedFees);
  if (summary.totalFees > 0) {
    if (feeLines.length === 0) {
      feeLines.push(toChargeLine("fee", "Fees", summary.totalFees));
    } else {
      const remainder = roundMoney(summary.totalFees - feesFromItemized);
      if (remainder > BILLING_MONEY_EPSILON) {
        feeLines.push(toChargeLine("fee", "Additional fees", remainder));
      }
    }
  }

  const discount = resolveOrderDiscount({
    hasOrderBillSummary,
    summaryTotalDiscount: summary.totalDiscount,
    itemizedDiscounts: discounts,
  });

  return {
    hasOrderBillSummary,
    subtotal: roundMoney(summary.subTotal),
    discount,
    feeLines,
    taxLines,
    grandTotal: roundMoney(summary.payableAmount),
    savingsSummary: order.savingsSummary,
  };
}
